import { useRequestAccountDeletion } from "@features/profile/mutations/useRequestAccountDeletion";
import { Dialog, DialogBackdrop, DialogPanel, DialogTitle } from "@headlessui/react";
import useCurrentUser from "@shared/queries/useCurrentUser";
import clsx from "clsx";
import { useState } from "react";

export default function DeleteAccountSection() {
  const { user } = useCurrentUser();
  const { mutate: requestDeletion, isPending, isSuccess, error } = useRequestAccountDeletion();
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);

  const alreadyRequested = Boolean(user?.deletionRequestedAt) || isSuccess;

  const handleConfirm = () => {
    requestDeletion(undefined, {
      onSuccess: () => setIsConfirmOpen(false),
    });
  };

  return (
    <div data-testid="delete-account-section">
      <h2 className="font-mono text-xl font-bold text-gray-700 mb-2">Delete account</h2>
      {alreadyRequested ? (
        <p className="text-sm text-gray-600" data-testid="delete-account-requested">
          We've received your deletion request. Your profile is hidden from discovery while we complete it.
        </p>
      ) : (
        <>
          <p className="mb-4 text-sm text-gray-600">
            Request deletion of your account. Your profile will be hidden from discovery. This does not sign you out
            immediately.
          </p>
          <button
            type="button"
            onClick={() => setIsConfirmOpen(true)}
            className={clsx(
              "inline-flex h-10 items-center px-4",
              "font-mono text-sm font-medium leading-none text-red-700",
              "rounded-button border-2 border-red-200 bg-white",
              "hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2",
              "transition-colors"
            )}
            data-testid="delete-account-button"
          >
            Delete account
          </button>
        </>
      )}

      {error ? (
        <p className="mt-3 text-sm text-red-600" data-testid="delete-account-error">
          {error.message}
        </p>
      ) : null}

      <Dialog
        open={isConfirmOpen}
        onClose={() => {
          if (!isPending) {
            setIsConfirmOpen(false);
          }
        }}
        className="relative z-50"
      >
        <DialogBackdrop className="fixed inset-0 bg-black/30 backdrop-blur-sm" />
        <div className="fixed inset-0 flex items-center justify-center p-4">
          <DialogPanel className="w-full max-w-md rounded-xl bg-white p-6 shadow-xl">
            <DialogTitle className="text-lg font-bold text-gray-900">Request account deletion?</DialogTitle>
            <p className="mt-2 text-sm text-gray-600">
              This sends a request to delete your account. Your profile will be hidden. An operator completes the
              deletion — you cannot undo this from the app.
            </p>
            <div className="mt-6 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setIsConfirmOpen(false)}
                disabled={isPending}
                className="h-10 rounded-lg px-4 font-mono text-sm font-medium text-gray-700 hover:bg-gray-100 disabled:opacity-50"
                data-testid="delete-account-cancel"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirm}
                disabled={isPending}
                className="h-10 rounded-lg bg-red-600 px-4 font-mono text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50"
                data-testid="delete-account-confirm"
              >
                {isPending ? "Sending…" : "Request deletion"}
              </button>
            </div>
          </DialogPanel>
        </div>
      </Dialog>
    </div>
  );
}
